import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { Background, Controls, Panel, ReactFlow, useEdgesState, useNodesState, useReactFlow } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import "./RuleBasedFlow.scss";
import AddNodeMenu from "./components/AddNodeMenu";
import InteractiveEdge from "./edges/InteractiveEdge";
import { useEdgeOperations } from "./hooks/useEdgeOperations";
import { useNodeOperations } from "./hooks/useNodeOperations";
import { useNodePositionPersistence } from "./hooks/useNodePositionPersistence";
import { useWorkflowData } from "./hooks/useWorkflowData";
import AssignNode from "./nodes/AssignNode";
import EmailNode from "./nodes/EmailNode";
import StarterNode from "./nodes/StarterNode";
import SwitchNode from "./nodes/SwitchNode";
import { STARTER_NODE_UI_ID } from "./ruleBasedFlow.constants";
import * as ruleBasedFlowService from "./ruleBasedFlow.service";
import { icon } from "@progress/kendo-react-common";
import { useSelector } from "react-redux";

const nodeTypes = {
  assignNode: AssignNode,
  switchNode: SwitchNode,
  starterNode: StarterNode,
  emailNode: EmailNode,
};

const edgeTypes = {
  interactive: InteractiveEdge,
};

const RuleBasedFlow = ({ context = "workflow", entityId: propEntityId }) => {
  const { workflowId: paramsWorkflowId } = useParams();
  const { IsTaskComplete} = useSelector((state) => state.taskSidebar.task);
  const { setViewport } = useReactFlow();

  // Determine entityId: use prop if provided, otherwise use params (backward compatible)
  const entityId = propEntityId || paramsWorkflowId;
  // Read-only when the task is marked complete
  const isReadOnly = useMemo(() => !!IsTaskComplete, [IsTaskComplete]);

  // Menu state
  const [showAddNodeMenu, setShowAddNodeMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [sourceNodeId, setSourceNodeId] = useState(null);
  const [sourceHandle, setSourceHandle] = useState(null);
  const [targetNodeId, setTargetNodeId] = useState(null); // For inserting between nodes
  const [firstNodeId, setFirstNodeId] = useState(null);
  const [rfInstance, setRfInstance] = useState(null);

  // React Flow state hooks
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Handlers (defined before custom hooks to avoid dependency issues)
  const handleStarterClick = useCallback((event) => {
    // Get button position and open menu beside it
    const rect = event.currentTarget.getBoundingClientRect();
    setMenuPosition({
      x: rect.right + 10, // 10px to the right of button
      y: rect.top,
    });
    setSourceNodeId(null);
    setSourceHandle(null);
    setShowAddNodeMenu(true);
  }, []);

  const handleNodeAddClick = useCallback((nodeId, handle, position) => {
    setMenuPosition({ x: 0, y: 0 });
    setSourceNodeId(nodeId);
    setSourceHandle(handle);
    setShowAddNodeMenu(true);
  }, []);

  const handleUpdateNodeDataEmail = useCallback(
    (nodeId, updatedData) => {
      setNodes((nds) =>
        nds.map((node) =>
          node.id === nodeId
            ? {
                ...node,
                data: {
                  ...node.data,
                  emailTemplate: updatedData.emailTemplate,
                },
              }
            : node
        )
      );
    },
    [setNodes]
  );

  /**
   * Handles adding a node between two existing nodes via edge button
   * @param {string} sourceNodeId - Source node ID
   * @param {string} targetNodeId - Target node ID
   * @param {string} sourceHandle - Source handle identifier
   * @param {Object} screenPosition - Screen coordinates for menu placement
   */
  const handleAddNodeBetween = useCallback((sourceNodeId, targetNodeId, sourceHandle, screenPosition) => {
    setMenuPosition({ x: screenPosition.x, y: screenPosition.y });
    setSourceNodeId(sourceNodeId);
    setSourceHandle(sourceHandle);
    setTargetNodeId(targetNodeId);
    setShowAddNodeMenu(true);
  }, []);

  // Edge operations (must come before node operations that use deleteEdge)
  // deleteEdge is defined inside the hook so all edges — loaded or freshly created — use the same real function
  const { onConnect, deleteEdge } = useEdgeOperations(entityId, context, nodes, edges, setEdges, setNodes, handleAddNodeBetween, setFirstNodeId);

  // Node operations (must come before handlers that use them)
  const { addNode, handleFirstNodeEdge, handleRegularNodeEdge, deleteNode, handleStarterEdgeAddNodeBetween } = useNodeOperations(
    entityId,
    context,
    firstNodeId,
    setFirstNodeId,
    setNodes,
    setEdges,
    handleAddNodeBetween,
    deleteEdge
  );

  const handleDeleteNodeClick = useCallback(
    async (nodeId , nodeType) => {
      await deleteNode(nodeId, nodeType);
    },
    [deleteNode]
  );
  const handleUpdateNodeData = useCallback(
    (nodeId, updatedData) => {
      setNodes((nds) =>
        nds.map((node) =>
          node.id === nodeId
            ? {
                ...node,
                data: {
                  ...node.data, // Preserve existing handlers and properties
                  ...updatedData, // Override with updated data
                },
              }
            : node
        )
      );
    },
    [setNodes]
  );

  // Load workflow/task data
  const { isLoading } = useWorkflowData(
    context,
    entityId,
    handleStarterClick,
    handleNodeAddClick,
    handleDeleteNodeClick,
    handleUpdateNodeData,
    setNodes,
    setEdges,
    setFirstNodeId,
    setViewport,
    handleAddNodeBetween,
    deleteEdge,
    handleUpdateNodeDataEmail,
    isReadOnly
  );

  // Position persistence hook (only for workflow context)
  const handlePositionChange = useNodePositionPersistence(context, entityId, isReadOnly, nodes);

  /**
   * Combined handler for node changes (handles both React Flow state and position persistence)
   */
  const handleNodesChange = useCallback(
    (changes) => {
      // First, let React Flow handle the state changes
      onNodesChange(changes);
      // Then, handle position persistence
      handlePositionChange(changes);
    },
    [onNodesChange, handlePositionChange]
  );

  /**
   * Auto-save flow view on unmount (only for workflow context)
   */
  useEffect(() => {
    return () => {
      // Cleanup: Save flow when component unmounts (only for workflow, not task)
      if (rfInstance && entityId && context === "workflow") {
        const flow = rfInstance.toObject();
        const payload = {
          workflowId: parseInt(entityId),
          nodesView: {
            ...flow,
            firstNodeId: firstNodeId, // Save firstNodeId with the flow
          },
        };

        // Fire and forget - don't wait for response
        ruleBasedFlowService
          .addUpdateWorkflowNodesView(payload)
          .then(() => console.log("Flow auto-saved on unmount"))
          .catch((err) => console.error("Error auto-saving flow:", err));
      }
    };
  }, [rfInstance, entityId, firstNodeId, context]);

  /**
   * Handles adding a new node from the menu
   * @param {string} nodeType - Type of node to add (assignNode, switchNode, emailNode)
   */
  const handleAddNode = useCallback(
    async (nodeType) => {
      const isFirstNodeToAdd = !firstNodeId;

      let nodePosition;
      if (targetNodeId && rfInstance) {
        const flowPosition = rfInstance.screenToFlowPosition({
          x: menuPosition.x,
          y: menuPosition.y,
        });
        nodePosition = { x: flowPosition.x - 100, y: flowPosition.y - 50 };
      } else {
        nodePosition = isFirstNodeToAdd ? { x: 400, y: 100 } : { x: 420, y: 120 };
      }

      // Check if this is a starter edge case (adding node between starter and first node)
      if (sourceNodeId === STARTER_NODE_UI_ID && targetNodeId) {
        const result = await handleStarterEdgeAddNodeBetween(nodeType, nodePosition, targetNodeId, sourceHandle);

        if (!result) {
          setShowAddNodeMenu(false);
          return;
        }

        const { savedNode } = result;

        // Update nodes state to add handlers to new node
        setNodes((nds) =>
          nds.map((node) =>
            node.id === savedNode._id
              ? {
                  ...node,
                  data: {
                    ...node.data,
                    onAddNode: handleNodeAddClick,
                    onDeleteNode: handleDeleteNodeClick,
                    onUpdateNodeData: handleUpdateNodeData,
                    onUpdateNodeDataEmail: handleUpdateNodeDataEmail,
                  },
                }
              : node
          )
        );

        // Update firstNodeId is already handled in handleStarterEdgeAddNodeBetween
        setTargetNodeId(null);
        setShowAddNodeMenu(false);
        return;
      }

      // Regular flow for non-starter edge cases
      const result = await addNode(nodeType, nodePosition, sourceNodeId, sourceHandle);

      if (!result) return;

      const { savedNode, isFirstNode } = result;

      setNodes((nds) =>
        nds.map((node) =>
          node.id === savedNode._id
            ? {
                ...node,
                data: {
                  ...node.data,
                  onAddNode: handleNodeAddClick,
                  onDeleteNode: handleDeleteNodeClick,
                  onUpdateNodeData: handleUpdateNodeData,
                  onUpdateNodeDataEmail: handleUpdateNodeDataEmail,
                },
              }
            : node
        )
      );

      if (isFirstNode) {
        handleFirstNodeEdge(savedNode._id);
      } else if (sourceNodeId) {
        const handle = sourceHandle || "bottom";
        await handleRegularNodeEdge(sourceNodeId, savedNode._id, handle);

        // If adding between nodes (targetNodeId is set), handle edge operations
        if (targetNodeId) {
          const oldEdge = edges.find((e) => e.source === sourceNodeId && e.target === targetNodeId);
          if (oldEdge) {
            await deleteEdge(oldEdge.id, sourceNodeId, targetNodeId);
          }

          if (nodeType !== "switchNode" && nodeType !== "emailNode") {
            await handleRegularNodeEdge(savedNode._id, targetNodeId, "bottom");
          }

          setTargetNodeId(null);
        }
      }

      setShowAddNodeMenu(false);
    },
    [
      addNode,
      sourceNodeId,
      sourceHandle,
      handleFirstNodeEdge,
      handleRegularNodeEdge,
      handleStarterEdgeAddNodeBetween,
      setNodes,
      handleNodeAddClick,
      handleDeleteNodeClick,
      firstNodeId,
      targetNodeId,
      deleteEdge,
      edges,
      menuPosition,
      rfInstance,
    ]
  );

  /**
   * Renders loading state
   */
  if (isLoading) {
    return (
      <div className='rule-based-flow-container'>
        <div className='flow-canvas' style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <p>Loading workflow...</p>
        </div>
      </div>
    );
  }

  return (
    <div className='rule-based-flow-container' style={context === "task" ? { height: "400px" } : { height: "calc(100vh - 240px)" }}>
      <div className='flow-canvas'>
        <div className='hide-react-flow-text'></div>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          panOnScroll={false}
          zoomOnScroll={false}
          onNodesChange={handleNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={isReadOnly ? undefined : onConnect}
          onInit={setRfInstance}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          fitView={context === "task"}
          deleteKeyCode={null}>
          <Background variant='dots' gap={12} size={1} />
          <Controls />

          {!isReadOnly && (
            <Panel position='top-left'>
              <button
                className='btn btn-light-custom d-flex align-items-center border'
                onClick={(e) => {
                  // Get ReactFlow container position
                  const flowContainer = e.currentTarget.closest(".react-flow");
                  if (flowContainer) {
                    const rect = flowContainer.getBoundingClientRect();
                    setMenuPosition({ x: rect.left + 20, y: rect.top + 60 }); // Top-left of canvas with padding
                  } else {
                    setMenuPosition({ x: 20, y: 60 });
                  }
                  setSourceNodeId(null);
                  setSourceHandle(null);
                  setShowAddNodeMenu(true);
                }}>
                <span className='ml-1'>+ Add Node</span>
              </button>
            </Panel>
          )}
        </ReactFlow>

        {showAddNodeMenu && <AddNodeMenu position={menuPosition} onSelect={handleAddNode} onClose={() => setShowAddNodeMenu(false)} />}
      </div>
    </div>
  );
};

export default RuleBasedFlow;
